import React, { useState, useEffect } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Chip, Divider } from '@mui/material';

// Convierte unidades cualitativas y de medida a valor numérico para la API de precio
const normalizeQuantity = (ingredient) => {
    if (typeof ingredient.equivalentAmountInBaseUnit === 'number') {
        return ingredient.equivalentAmountInBaseUnit;
    }

    const { quantity, selectedUnit } = ingredient;
    const unit = selectedUnit?.toLowerCase() || '';
    const num = typeof quantity === 'number' ? quantity : parseFloat(String(quantity).replace(',', '.'));

    if (isNaN(num)) return 0;

    const apiBase = unit.includes('l') ? 'l' : 'kg';

    const qualitative = {
        pizca: { kg: 0.001, l: 0.001 },
        cucharadas: { kg: 0.015, l: 0.015 },
        chorro: { l: 0.025 },
        unidades: { dc: 1 },
    };

    if (unit === apiBase) return num;
    if ((unit === 'g' || unit === 'gr' || unit === '100 g') && apiBase === 'kg') return num / 1000;
    if ((unit === 'ml' || unit === '100 ml') && apiBase === 'l') return num / 1000;
    if (qualitative[unit]?.[apiBase]) return qualitative[unit][apiBase];

    return num;
};

function RecipeDetailComponent({ recipe }) {
    const defaultImage = '/static/images/recipes/default.jpg';
    const [pricedIngredients, setPricedIngredients] = useState([]);
    const [totalPrice, setTotalPrice] = useState(null);

    useEffect(() => {
        const backendUrl = process.env.REACT_APP_INGREDIENTS_API_URL || "http://localhost:8000";
        const fetchPrices = async () => {
            const enriched = await Promise.all(
                recipe.ingredients.map(async ing => {
                    const qtyForApi = normalizeQuantity(ing);
                    try {
                        const res = await fetch(
                            `${backendUrl}/product/${ing.id}/provider/${ing.providerId}/price?quantity=${qtyForApi}`
                        );
                        if (!res.ok) throw new Error();
                        const data = await res.json();
                        const priceValue = data.result ?? 0;
                        return { ...ing, price: priceValue };
                    } catch {
                        return { ...ing, price: 0 };
                    }
                })
            );
            setPricedIngredients(enriched);
            const sum = enriched.reduce((acc, i) => acc + (i.price || 0), 0);
            setTotalPrice(sum);
        };
        fetchPrices();
    }, [recipe.ingredients]);

    // Renderizado de ingredientes
    const listItems = pricedIngredients.map((ing, idx) => (
            <ListItem sx={{ display: 'list-item' }} key={idx}>
                <ListItemText
                    primary={`${ing.name}${ing.unitMeasure ? ` (${ing.unitMeasure})` : ''}`}
                    secondary={`Cantidad: ${ing.quantity} ${ing.selectedUnit} — Precio: €${ing.price?.toFixed(2)}`}
                />
            </ListItem>
        ));

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            {/* Título y Precio Total Visible */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h3" component="h1">
                    {recipe.title}
                </Typography>
                {totalPrice != null && (
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        Coste aproximado: €{totalPrice.toFixed(2)}
                    </Typography>
                )}
            </Box>

            {recipe.image && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img
                        src={recipe.image}
                        alt={recipe.title}
                        style={{ maxWidth: '100%', borderRadius: 8 }}
                        onError={e => {
                            if (e.target.src !== defaultImage) {
                                e.target.onerror = null;
                                e.target.src = defaultImage;
                            }
                        }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'gray', textAlign: 'left' }}>
                        Autor: {recipe.author?.username}
                    </Typography>
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h5" sx={{ mb: 1 }}>
                Número de comensales: {recipe.peopleNumber}
            </Typography>

            <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
                Ingredientes
            </Typography>
            <List sx={{ listStyle: 'circle', pl: 4, mb: 2 }}>{listItems}</List>

            <Divider sx={{ mb: 3 }} />

            {recipe.prerequisites?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        Requisitos Previos
                    </Typography>
                    <List sx={{ listStyle: 'circle', pl: 4 }}>
                        {recipe.prerequisites.map((pr, idx) => (
                            <ListItem sx={{ display: 'list-item' }} key={idx}>
                                <ListItemText primary={pr} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            <Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    Preparación
                </Typography>
                <List sx={{ listStyle: 'decimal', pl: 4 }}>
                    {recipe.preparation.map((step, idx) => (
                        <ListItem sx={{ display: 'list-item' }} key={idx}>
                            <ListItemText primary={step} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {recipe.tags?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        Etiquetas
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {recipe.tags.map((tag, idx) => (
                            <Chip key={idx} label={tag} />
                        ))}
                    </Box>
                </Box>
            )}
        </Container>
    );
}

export default RecipeDetailComponent;
